import { Box, CircularProgress, Container, Typography } from '@mui/material';
import { isEmpty, isUndefined } from 'lodash';
import { FC, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { casesApi } from 'src/api/casesApi';
import { projectApi } from 'src/api/projectApi';
import ProjectsSubHeader from 'src/components/organization/projects/ProjectsSubHeader';
import getProjectTypeShort from 'src/components/organization/projects/projectTypes';
import CustomizedTable from 'src/components/tables/CustomizedTable';
import { BillingType } from 'src/components/tables/renderers/BillingType';
import { Priority } from 'src/components/tables/renderers/Priority';
import { Status } from 'src/components/tables/renderers/Status';
import { RootState } from 'src/store/store';
import useGenericStyles from 'src/styles/useGenericStyles';
import { TableProps } from 'src/types/components/tableList.types';
import capitalize from 'src/utils/capitalize';
import convertSecondsToTimeString from 'src/utils/convertSecondsToTimeString';
import setTitle from 'src/utils/setTitle';
import { fetchCompanyDetails } from '../../company-details';
import { getClientDetailsHref } from '../../clientDetailsTabs';

const DEFAULT_VISIBLE_COLUMNS = ["caseKey", "summary", "createdBy", "caseType", "project", "priority", "status"];
const FILTER_STORAGE_KEY = 'casesByProjectFilters';

const ProjectDetails: FC<TableProps> = () => {
  // const location = useLocation();

  const classes = useGenericStyles();
  setTitle('ITSM | Tickets By Projects');
  const allProjects = useSelector(
    (state: RootState) => state.projects.allProjects
  );
  const params = useParams();
  const { projectID, companyId } = params;

  const [companyDetails, setCommpanyDetails] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null);
  const [casesByProj, setCasesByProj] = useState<any>([]);
  const [hasAttachments, setHasAttachments] = useState(false);
  const [page, setPage] = useState<any>(
    JSON.parse(localStorage.getItem('casesByProjPagePg'))?.casesByProjPageNo || 0
  );
  const [pageSize, setPageSize] = useState<any>(
    JSON.parse(localStorage.getItem('casesByProjPagePg'))?.casesByProjPageLimit || 25
  );
  const [search, setSearch] = useState<string>(
    JSON.parse(localStorage.getItem('casesByProjPagePg'))?.search || ''
  );
  const [totalCount, setTotalCount] = useState<number>(0);
  const [filters, setFilters] = useState<any>({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // const [projectsAutocomplete, setprojectsAutocomplete] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [internalUsers, setAllInternalUsers] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [sort, setSort] = useState<any>({});
  const [fetchingData, setFetchingData] = useState(false);
  const [issueTypes, setIssueTypes] = useState([]);
  const [allBillingAccounts, setAllBillingAccounts] = useState([]);
  const [allSprintsByOrganization, setAllSprintsByOrganization] = useState([]);

  const handleSearch = (val) => {
    setSearch(val);
  };

  const customColumns = [
    {
      field: 'caseKey',
      headerName: 'TICKET#',
      width: '10%',
      maxWidth: '10%',
      sortable: true,
      filterable: true,
    },
    {
      field: 'summary',
      headerName: 'Title',
      maxWidth: '400px',
      render: (columnValue, data) => (
        <Typography
          title={capitalize(data?.summary)}
          style={{
            fontFamily: 'Inter',
            fontSize: 12,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {capitalize(data?.summary)}
        </Typography>
      ),
      sortable: true,
      filterable: true,
    },
    {
      field: 'createdBy',
      headerName: 'Reporter',
      maxWidth: '10%',
      width: '10%',
      render: (columnValue) => (
        <Typography
          style={{
            fontFamily: 'Inter',
            fontSize: 12,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {columnValue?.name}
        </Typography>
      ),
      sortable: true,
      filterable: true,
    },
    {
      field: 'caseType',
      headerName: 'Type',
      render: (columnValue) => (
        <Box
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={capitalize(columnValue?.name)}>
          {getProjectTypeShort(columnValue?.name) ?? ''}
        </Box>
      ),
      maxWidth: '10%',
      width: '10%',
      sortable: true,
      filterable: true,
    },
    {
      field: 'timeEstimate',
      headerName: 'Est.',
      maxWidth: '10%',
      width: '10%',
      render: (columnValue) => (
        <Box
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color:
              capitalize(
                columnValue ? convertSecondsToTimeString(columnValue) : ''
              ) === 'N/A'
                ? 'red'
                : 'initial',
          }}
          title={
            capitalize(convertSecondsToTimeString(columnValue)) === 'N/A'
              ? 'Hours not quoted'
              : capitalize(convertSecondsToTimeString(columnValue))
          }>
          {capitalize(
            columnValue ? convertSecondsToTimeString(columnValue) : ''
          )}
        </Box>
      ),
      sortable: true,
    },
    {
      field: 'project',
      headerName: 'Project',
      render: (columnValue) => (
        <span
          style={{
            fontSize: '12px',
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {capitalize(columnValue?.name ?? '')}
        </span>
      ),
      width: '12%',
      maxWidth: '12%',
      sortable: true,
      filterable: true,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      maxWidth: '10%',
      width: '9%',
      render: (columnValue) => (
        <Priority priority={capitalize(columnValue?.name ?? '')} />
      ),
      sortable: true,
      filterable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      maxWidth: '10%',
      width: '11%',
      render: (columnValue) => (
        <Status status={capitalize(columnValue?.name ?? '')} />
      ),
      sortable: true,
      filterable: true,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      maxWidth: '12%',
      width: '12%',
      render: (columnValue) => (
        <Box>{new Date(columnValue).toLocaleString()}</Box>
      ),
      sortable: true,
    },
    {
      field: 'issueType',
      headerName: 'Issue Type',
      maxWidth: '10%',
      width: '10%',
      render: (columnValue) => <Box>{columnValue?.name}</Box>,
      sortable: true,
      filterable: true,
    },
    {
      field: 'expense',
      headerName: 'Expense',
      maxWidth: '8%',
      width: '8%',
      render: (columnValue) =>
        columnValue ? <BillingType value={columnValue} /> : 'N/A',
      sortable: true,
    },
    {
      field: 'billingAccountType',
      headerName: 'Account',
      maxWidth: '12%',
      width: '12%',
      render: (columnValue) => <Box>{columnValue?.name}</Box>,
      sortable: true,
      filterable: true,
    },
    {
      field: 'isApproved',
      headerName: 'Approved',
      maxWidth: '10%',
      width: '10%',
      render: (columnValue, data) => <Box>{!isEmpty(data?.caseType) &&
        !isUndefined(data?.caseType) &&
        data?.caseType?.name === 'Application Feature Request' ? (
        columnValue ? (
          'Approved'
        ) : (
          'Not Approved'
        )
      ) : (
        'Approval Not Required'
      )}</Box>,
      sortable: true,
    },
    {
      field: 'approvedBy',
      headerName: 'Approved By',
      maxWidth: '12%',
      width: '12%',
      render: (columnValue) => <Box>{columnValue?.name || 'N/A'}</Box>,
      sortable: true,
    },
    {
      field: 'approvedDate',
      headerName: 'Approved Date',
      maxWidth: '12%',
      width: '12%',
      render: (columnValue) => (
        <Box>{columnValue ? new Date(columnValue).toLocaleString() : 'N/A'}</Box>
      ),
      sortable: true,
    },
    {
      field: 'docUrls.figmaLinks',
      headerName: 'Figma Links',
      maxWidth: '20%',
      width: '20%',
      render: (_, data) => (
        <Box style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          fontSize: '12px'
        }}>
          {data.docUrls?.figmaLinks?.length > 0 ? (
            data.docUrls.figmaLinks.map((link, index) => (
              <Box
                key={link.id || `${link.url}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                <span>•</span>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#2563EB',
                    textDecoration: 'none'
                  }}
                >
                  {link.label || link.url}
                </a>
              </Box>
            ))
          ) : (
            'No links'
          )}
        </Box>
      ),
    },
    {
      field: 'docUrls.googleDocLinks',
      headerName: 'Google Links',
      maxWidth: '20%',
      width: '20%',
      render: (_, data) => (
        <Box style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          fontSize: '12px'
        }}>
          {data.docUrls?.googleDocLinks?.length > 0 ? (
            data.docUrls.googleDocLinks.map((link, index) => (
              <Box
                key={link.id || `${link.url}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                <span>•</span>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#2563EB',
                    textDecoration: 'none'
                  }}
                >
                  {link.label || link.url}
                </a>
              </Box>
            ))
          ) : (
            'No links'
          )}
        </Box>
      ),
    },
    {
      field: 'sprint',
      headerName: 'Sprint',
      maxWidth: '10%',
      width: '10%',
      render: (columnValue) => <Box>{columnValue?.name}</Box>,
      sortable: true,
      filterable: true,
    },
    {
      field: 'releaseVersion',
      headerName: 'Fix Version',
      maxWidth: '10%',
      width: '10%',
      render: (columnValue) => (
        <Box>{columnValue?.map(v => v.name).join(', ')}</Box>
      ),
      sortable: true,
    },
  ];

  const getFilterOptions = (column) => {
    switch (column) {
      case 'createdBy':
        return internalUsers.map((userObj) => ({
          label: userObj.name,
          value: userObj.id,
        }));
      case 'caseType':
        return caseTypes.map((typeObj) => ({
          label: typeObj.name,
          value: typeObj.id,
        }));
      case 'project':
        return allProjects.map((projectObj) => ({
          label: projectObj.name,
          value: projectObj.id,
        }));
      case 'caseKey':
        return casesByProj.map((projectObj) => ({
          label: projectObj.caseKey,
          value: projectObj.caseKey,
        }));
      case 'summary':
        return casesByProj.map((projectObj) => ({
          label: projectObj.summary,
          value: projectObj.summary,
        }));
      case 'priority':
        return priorityOptions?.map((priority) => ({
          label: priority?.name,
          value: priority?.id,
        }));
      case 'status': {
        return statusOptions?.map((status) => ({
          label: status?.name,
          value: status?.id,
        }));
      }
      case 'issueType': {
        return issueTypes.map((status) => ({
          label: status?.name,
          value: status?.id,
        }));
      }
      case 'billingAccountType': {
        return allBillingAccounts.map((status) => ({
          label: status?.name,
          value: status?.id,
        }));
      }
      case 'sprint': {
        return allSprintsByOrganization.map((status) => ({
          label: status?.name,
          value: status?.id,
        }));
      }
      case 'isApproved':
        return [
          {
            label: 'Approved',
            value: true,
          },
          {
            label: 'Not Approved',
            value: false,
          },
        ];
      default:
        return [];
    }
  };

  const handlePageSize = (temp: number) => {
    setPageSize(temp);
  };

  const handlePage = (temp: number) => {
    setPage(temp);
  };

  const handleSwitchChange = (checked: boolean) => {
    setHasAttachments(checked);
  };

  // const formatDate = (date) => {
  //   if (!date) return null;
  //   const isoString = date.toISOString();
  //   return isoString.split('T')[0];
  // };

  const handleSort = (field) => {
    setSort((prevSort) => ({
      field,
      order:
        prevSort.field === field && prevSort.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getAllProjectsAutoComplete = async () => {
    try {
      const response = await projectApi.getAllAutocomplete();
      if (response.status === 200) {
        // setprojectsAutocomplete(response.data);
        console.warn("response.data");
      }
    } catch (error) {
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('An error occured while fetching projects');
      }
    }
  };

  const getAllPrioritiesAutoComplete = async () => {
    try {
      const response = await casesApi.getPriorities();
      if (response.status === 200) {
        setPriorityOptions(response.data);
      }
    } catch (error) {
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('An error occured while fetching types');
      }
    }
  };

  const getAllStatusAutoComplete = async () => {
    try {
      const response = await casesApi.getStatuses();
      if (response.status === 200) {
        setStatusOptions(response.data);
      }
    } catch (error) {
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('An error occured while fetching types');
      }
    }
  };

  const getAllTypesAutoComplete = async () => {
    try {
      const response = await casesApi.getCaseTypes();
      if (response.status === 200) {
        setCaseTypes(response.data);
      }
    } catch (error) {
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('An error occured while fetching types');
      }
    }
  };

  const getAllInternalUsersAutoComplete = async () => {
    try {
      const res = await casesApi?.getAutoCompleteUser(true);
      setAllInternalUsers(res.data);
    } catch (error) {
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('An error occured while fetching users');
      }
    }
  };

  const getFavProjects = async () => {
    try {
      const response = await projectApi.getAllFavouriteProjects();
      if (response?.status === 200) {
        console.warn(response.status);
      }
    } catch (error) {
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('An error occured while fetching projects');
      }
    }
  };

  const getIssueTypesAutoComplete = async () => {
    try {
      const res = await casesApi?.getIssueTypes();
      setIssueTypes(res.data);
    } catch (error) {
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('An error occured while fetching issue types');
      }
    }
  };

  const getAllBillingAccounts = async () => {
    try {
      const res = await casesApi?.getAllBillingAccounts();
      setAllBillingAccounts(res.data);
    } catch (error) {
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('An error occured while fetching billing accounts');
      }
    }
  };

  const getAllSprintsByOrganization = async () => {
    try {
      const res = await casesApi?.getSprintsByOrganization();
      setAllSprintsByOrganization(res.data);
    } catch (error) {
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('An error occured while fetching sprints');
      }
    }
  };

  const getCasesByProject = async () => {
    setFetchingData(true);
    try {
      const response = await casesApi.getCasesCardsByProj({ page, size: pageSize, projectId: projectID });

      setCasesByProj(response?.data?.data || []);
      setTotalCount(response?.totalRecords || 0);
      setFetchingData(false);
    } catch (error) {
      setFetchingData(false);
      toast.error(error);
      setCasesByProj([]);
      setTotalCount(0);
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const loadFiltersFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem(FILTER_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setFilters(parsed.filters || {});
        setSearch(parsed.search || '');
        setHasAttachments(parsed.hasAttachments || false);
        setStartDate(parsed.startDate ? new Date(parsed.startDate) : null);
        setEndDate(parsed.endDate ? new Date(parsed.endDate) : null);
      }
    } catch (error) {
      console.error('Failed to load filters from localStorage:', error);
    }
  };

  const saveFiltersToLocalStorage = () => {
    try {
      const filterData = {
        filters,
        search,
        hasAttachments,
        startDate: startDate?.toISOString() || null,
        endDate: endDate?.toISOString() || null,
      };
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterData));
    } catch (error) {
      console.error('Failed to save filters to localStorage:', error);
    }
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearch('');
    setHasAttachments(false);
    setStartDate(null);
    setEndDate(null);
    localStorage.removeItem(FILTER_STORAGE_KEY);
  };

  const handleRemoveFilter = (column) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      delete newFilters[column];
      try {
        const stored = localStorage.getItem(FILTER_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.filters) {
            delete parsed.filters[column];
            localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(parsed));
          }
        }
      } catch (error) {
        console.error('Failed to update filters in localStorage:', error);
      }
      return newFilters;
    });
  };

  const handleApplyFilter = (
    currentFilterColumn,
    filterValue,
    filterName,
    checked
  ) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      if (checked) {
        if (newFilters[currentFilterColumn]) {
          newFilters[currentFilterColumn].push({
            label: filterName,
            value: filterValue,
          });
        } else {
          newFilters[currentFilterColumn] = [
            {
              label: filterName,
              value: filterValue,
            },
          ];
        }
      } else if (newFilters[currentFilterColumn]) {
        newFilters[currentFilterColumn] = newFilters[
          currentFilterColumn
        ].filter((obj) => obj.value !== filterValue);

        if (newFilters[currentFilterColumn].length === 0) {
          delete newFilters[currentFilterColumn];
        }
      }
      return newFilters;
    });
  };


  useEffect(() => {
    loadFiltersFromLocalStorage();
  }, []);

  useEffect(() => {
    getCasesByProject();
  }, [
    sort,
    page,
    pageSize,
    search,
    JSON.stringify(filters),
    hasAttachments,
    startDate,
    endDate,
    projectID,
  ]);
  const getCompanyDetails = async () => {
    const detailsData = await fetchCompanyDetails({ companyId });
    if (detailsData) setCommpanyDetails(detailsData);
  };

  const getProjectDetails = async () => {
    try {
      const projDetails = await projectApi.getProjectDetailsById(projectID);
      if (projDetails?.data) setProjectDetails(projDetails.data)
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    if (companyId) {
      getCompanyDetails();
      getProjectDetails();
    }
  }, [companyId]);

  useEffect(() => {
    getCompanyDetails();
    getAllProjectsAutoComplete();
    getAllInternalUsersAutoComplete();
    getAllTypesAutoComplete();
    getFavProjects();
    getAllPrioritiesAutoComplete();
    getAllStatusAutoComplete()
    getIssueTypesAutoComplete();
    getAllBillingAccounts();
    getAllSprintsByOrganization();
  }, []);

  useEffect(() => {
    if (
      Object.keys(filters).length > 0 ||
      search ||
      hasAttachments ||
      startDate ||
      endDate
    ) {
      saveFiltersToLocalStorage();
    }
  }, [filters, search, hasAttachments, startDate, endDate]);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const savedColumns = localStorage.getItem('cases-by-projects-columns');
    return savedColumns ? JSON.parse(savedColumns) : DEFAULT_VISIBLE_COLUMNS;
  });

  const breadcrumbData = {
    links: [
      { name: 'Clients', to: '/clients' },
      { name: companyDetails?.name, to: companyDetails?.id ? getClientDetailsHref(String(companyDetails.id)) : '' },
      { name: projectDetails?.name, to: `/projects/${projectDetails?.id}` },
    ]
  }

  return (
    <>
      <Container maxWidth={false} className={classes.container}>
        <ProjectsSubHeader
          projectName={projectDetails?.name}
          search={search}
          onSearch={handleSearch}
          filters={filters}
          onRemoveFilter={handleRemoveFilter}
          hasAttachments={hasAttachments}
          onSwitchChange={handleSwitchChange}
          startDate={startDate}
          onStartDateChange={handleStartDateChange}
          endDate={endDate}
          onEndDateChange={handleEndDateChange}
          onClearFilters={handleClearFilters}
          breadcrumbData={breadcrumbData}
        />
        <CustomizedTable
          heading={
            projectDetails?.name ? (
              `${projectDetails?.name} Tickets`
            ) : (
              <Box
                style={{
                  color: '#8492A6',
                }}>
                <CircularProgress size={20} color="inherit" />
              </Box>
            )
          }
          loading={fetchingData}
          columns={customColumns}
          page={page}
          limit={pageSize}
          handleLimit={handlePageSize}
          handlePage={handlePage}
          data={casesByProj}
          totalRecords={totalCount}
          getFilterOptions={getFilterOptions}
          handleSort={handleSort}
          sort={sort}
          handleApplyFilter={handleApplyFilter}
          filters={filters}
          navigateUrl="/cases"
          navigateToKey
          selectRowData
          customizableColumns
          defaultVisibleColumns={visibleColumns}
          onColumnChange={(newColumns) => setVisibleColumns(newColumns)}
          storageKey="cases"
        />
      </Container>
    </>
  );
};

export default ProjectDetails;